import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Title,
  Text,
  Group,
  Badge,
  Stack,
  RingProgress,
  LoadingOverlay,
  Collapse,
  UnstyledButton,
} from '@mantine/core';
import { PageContainer } from '../../../components/ui/PageContainer';
import { LoadFailed } from '../../../components/ui/LoadFailed';
import { IconArrowLeft, IconChevronRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Grade, Swimlane, CompetencyMap, Competency, UserCompetency } from '../../../types';
import { CompetencyCard } from '../../../components/competency/CompetencyCard';
import { CompetencyDrawer } from '../../../components/competency/CompetencyDrawer';
import { api } from '../../../utils/api';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../../store/userStore';
import { useAnalytics, AnalyticsEvents } from '../../../analytics';

type CompetencyLink = {
  competency?: Partial<Competency> & { _id?: string };
  gradeId?: string;
  swimlaneId?: string;
  order?: number;
  isRequired?: boolean;
};

type GradeFromApi = Omit<Grade, 'competencies'> & { competencies?: Competency[] };

type CompetencyMapFromApi = Omit<CompetencyMap, 'grades'> & {
  grades?: GradeFromApi[];
  competencies?: CompetencyLink[];
};

const normalizeMap = (map: CompetencyMapFromApi): CompetencyMap => {
  const linksByGrade = new Map<string, Competency[]>();

  for (const link of map.competencies || []) {
    if (!link.gradeId || !link.swimlaneId || !link.competency?._id) continue;

    const competency: Competency = {
      _id: link.competency._id,
      title: link.competency.title || '',
      description: link.competency.description,
      tags: link.competency.tags,
      swimlaneId: link.swimlaneId,
      sortOrder: link.order,
      mentors: link.competency.mentors,
      isRequired: link.isRequired || false,
      dependencies: link.competency.dependencies,
      recommendedCourses: link.competency.recommendedCourses,
    };

    linksByGrade.set(link.gradeId, [...(linksByGrade.get(link.gradeId) || []), competency]);
  }

  return {
    ...map,
    grades: (map.grades || []).map((grade) => {
      const linkedCompetencies = linksByGrade.get(grade._id) || [];
      return {
        ...grade,
        competencies: grade.competencies?.length ? grade.competencies : linkedCompetencies,
      };
    }),
    swimlanes: map.swimlanes || [],
    professions: map.professions || [],
  };
};

export const GradeDetail: React.FC = () => {
  const { mapId, gradeId } = useParams<{ mapId: string; gradeId: string }>();
  const navigate = useNavigate();
  const [competencyMap, setCompetencyMap] = useState<CompetencyMap | null>(null);
  const [gradeData, setGradeData] = useState<Grade | null>(null);
  const [swimlanes, setSwimlanes] = useState<Swimlane[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openedSwimlanes, setOpenedSwimlanes] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState<{
    comp: Competency;
    gradeIdx: number;
    swimlane: Swimlane;
  } | null>(null);
  const { t } = useTranslation();
  const user = useUserStore((s) => s.user);
  const checkSession = useUserStore((s) => s.checkSession);
  const { track } = useAnalytics();
  const userCompetencies: UserCompetency[] = user?.competencies || [];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Обновляем данные пользователя
        await checkSession();

        if (!mapId) return;
        const map = normalizeMap(await api(`/competency-maps/${mapId}`));
        setCompetencyMap(map);
        setSwimlanes(map.swimlanes || []);
        const grade = (map.grades || []).find((g: Grade) => g._id === gradeId);
        if (grade) {
          setGradeData(grade);
          track(AnalyticsEvents.COMPETENCY_MAP_VIEWED, {
            mapId,
            gradeId,
            viewMode: 'grade-detail',
          });
          // Открываем первый свимлейн с компетенциями
          const firstSwimlaneWithCompetencies = map.swimlanes.find((swimlane: Swimlane) =>
            grade.competencies?.some((comp: Competency) => comp.swimlaneId === swimlane._id),
          );
          if (firstSwimlaneWithCompetencies) {
            setOpenedSwimlanes(new Set([firstSwimlaneWithCompetencies._id]));
          }
        } else {
          setGradeData(null);
        }
      } catch (error) {
        setGradeData(null);
        setCompetencyMap(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // checkSession and track are stable refs (Zustand store / memoized hook);
    // do not include them to avoid re-running fetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, gradeId]);

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingOverlay visible={true} />
      </PageContainer>
    );
  }

  if (!gradeData) {
    return (
      <PageContainer>
        <LoadFailed onBack={() => navigate(`/competency-maps/${mapId}`)} />
      </PageContainer>
    );
  }

  // Вычисляем статистику
  const totalCompetencies = gradeData.competencies.length;
  const acquiredCompetencies = gradeData.competencies.filter((comp) =>
    userCompetencies.some((uc) => uc.competencyId === comp._id),
  ).length;
  const progress =
    totalCompetencies > 0 ? Math.round((acquiredCompetencies / totalCompetencies) * 100) : 0;

  return (
    <PageContainer>
      <Group justify="space-between" align="flex-start" mb="lg">
        <div style={{ flex: 1 }}>
          <Group gap="md" align="center" mb="md">
            <IconArrowLeft
              size={20}
              style={{ cursor: 'pointer' }}
              // Возвращаемся к таблице грейдов того класса, чей грейд открыт
              // (например, при просмотре из перформанс-ревью другого человека),
              // а не к /grades — карте класса самого зрителя.
              onClick={() => navigate(`/competency-maps/${mapId}`)}
            />
            <Title order={1}>{gradeData.title}</Title>
            <Badge color="teal" variant="light" size="lg">
              {gradeData.qualification}
            </Badge>
          </Group>

          <Text c="dimmed" size="sm" mb={{ base: 'md', sm: 'xl' }}>
            {gradeData.description}
          </Text>

          <Group gap="xl" mb="lg" align="flex-start">
            <div>
              <Text size="sm" c="dimmed" mb={4}>
                {t('gradeDetail.progress')}
              </Text>
              <Group gap="md" align="center">
                <RingProgress
                  size={60}
                  thickness={4}
                  sections={[{ value: progress, color: 'teal' }]}
                  label={
                    <Text ta="center" size="xs" fw={700}>
                      {progress}%
                    </Text>
                  }
                />
                <div>
                  <Text size="sm" fw={500}>
                    {acquiredCompetencies} {t('common.of')} {totalCompetencies}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {t('gradeDetail.competenciesReceived')}
                  </Text>
                </div>
              </Group>
            </div>

            <div>
              <Text size="sm" c="dimmed" mb={4}>
                {t('gradeDetail.position')}
              </Text>
              <Text size="sm" fw={500}>
                {gradeData.position}
              </Text>
            </div>
          </Group>
        </div>
      </Group>

      <Stack gap="xs">
        <Title order={3} mb="md">
          {t('gradeDetail.competencies', { count: totalCompetencies })}
        </Title>

        {swimlanes.map((swimlane) => {
          const competenciesInSwimlane = gradeData.competencies.filter(
            (comp) => comp.swimlaneId === swimlane._id,
          );

          if (competenciesInSwimlane.length === 0) return null;

          const isOpened = openedSwimlanes.has(swimlane._id);

          return (
            <div key={swimlane._id}>
              <UnstyledButton
                onClick={() => {
                  setOpenedSwimlanes((prev) => {
                    const newSet = new Set(prev);
                    if (isOpened) {
                      newSet.delete(swimlane._id);
                    } else {
                      newSet.add(swimlane._id);
                    }
                    return newSet;
                  });
                }}
                style={{
                  width: '100%',
                  padding: '8px 0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <IconChevronRight
                  size={16}
                  style={{
                    transform: isOpened ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
                <Text fw={600} size="lg" opacity={0.7}>
                  {swimlane.title} ({competenciesInSwimlane.length})
                </Text>
              </UnstyledButton>

              <Collapse in={isOpened}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '16px',
                    marginTop: '16px',
                    marginBottom: '16px',
                  }}
                >
                  {competenciesInSwimlane.map((competency) => {
                    const userCompetency = userCompetencies.find(
                      (uc) => uc.competencyId === competency._id,
                    );
                    const hasCompetency = !!userCompetency;
                    return (
                      <CompetencyCard
                        key={competency._id}
                        comp={competency}
                        gradeIdx={0}
                        swimlane={swimlane}
                        onClick={() => setDrawer({ comp: competency, gradeIdx: 0, swimlane })}
                        dragProps={{}}
                        dragHandleProps={{}}
                        isDragging={false}
                        userHas={hasCompetency}
                        userCompetency={userCompetency}
                        isDragDropEnabled={false}
                        isRequired={competency.isRequired}
                        cardSize="L"
                      />
                    );
                  })}
                </div>
              </Collapse>
            </div>
          );
        })}
      </Stack>
      <CompetencyDrawer
        drawer={drawer}
        onClose={() => setDrawer(null)}
        grades={competencyMap?.grades?.map((g) => g.title) || []}
        userCompetencies={userCompetencies}
        competencyMapId={competencyMap?._id}
        hideStatusAndActions={false}
        currentUser={user}
      />
    </PageContainer>
  );
};
